import React, { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import DashboardShell from "@/components/layout/SmartShell";
import CreditCostStrip from "@/components/credits/CreditCostStrip";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Sparkles, Megaphone, Wand2 } from "lucide-react";

/**
 * Guided Ad Generator
 * --------------------------------------------------------------
 * The blank-prompt killer. Users pick category, tone, audience,
 * format, etc. and the system returns: concept + hook + tagline +
 * VO script + scene-by-scene breakdown + visual prompts +
 * render-ready credit estimate.
 *
 * The render-ready "Generate first shot" CTA pre-seeds the prompt
 * into /generate/video so the user goes from blank canvas to a
 * real Sora 2 clip in a single guided arc.
 */

const CATEGORIES = [
  { id: "watch",      label: "Watch / Jewelry" },
  { id: "fashion",    label: "Fashion / Apparel" },
  { id: "skincare",   label: "Skincare / Beauty" },
  { id: "cosmetics",  label: "Cosmetics / Lipstick" },
  { id: "food",       label: "Food / Beverage" },
  { id: "tech",       label: "Tech / Electronics" },
  { id: "fitness",    label: "Fitness / Wellness" },
  { id: "automotive", label: "Automotive" },
  { id: "real_estate",label: "Real Estate / Property" },
  { id: "service",    label: "Service / SaaS" },
  { id: "general",    label: "Other / General" },
];

const TONES = [
  { id: "cinematic", label: "Cinematic" },
  { id: "luxury",    label: "Luxury" },
  { id: "bold",      label: "Bold / High-energy" },
  { id: "clean",     label: "Clean / Minimal" },
  { id: "premium",   label: "Premium" },
  { id: "playful",   label: "Playful" },
  { id: "funny",     label: "Funny / Witty" },
  { id: "documentary",label: "Documentary" },
];

const FORMATS = [
  { id: "commercial", label: "Commercial" },
  { id: "promo",      label: "Brand Promo" },
  { id: "social",     label: "Social Spot" },
  { id: "ugc",        label: "UGC-style" },
];

const PLATFORMS = [
  { id: "instagram", label: "Instagram / Reels" },
  { id: "tiktok",    label: "TikTok" },
  { id: "youtube",   label: "YouTube / Pre-roll" },
  { id: "tv",        label: "TV / Broadcast" },
  { id: "open-web",  label: "Web / Open" },
];

const DURATIONS = [
  { id: 10, label: "10s — hook" },
  { id: 15, label: "15s — standard" },
  { id: 30, label: "30s — full ad" },
  { id: 60, label: "60s — long-form" },
];

export default function AdGeneratorPage() {
  const { user } = useAuth();
  const [product,    setProduct]    = useState("");
  const [category,   setCategory]   = useState("watch");
  const [tone,       setTone]       = useState("cinematic");
  const [audience,   setAudience]   = useState("");
  const [style,      setStyle]      = useState("modern");
  const [format,     setFormat]     = useState("commercial");
  const [platform,   setPlatform]   = useState("instagram");
  const [duration,   setDuration]   = useState(15);
  const [references, setReferences] = useState("");

  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState("");
  const [result,  setResult]  = useState(null);

  if (!user) return <Navigate to="/login" replace />;

  const onGenerate = async () => {
    setError(""); setResult(null);
    if (!product.trim()) { setError("Tell us what product or service this ad is for."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/ads/brief", {
        product:    product.trim(),
        category, tone,
        audience:   audience.trim() || "general",
        style:      style.trim() || "modern",
        format, platform,
        duration_s: duration,
        references: references.trim(),
      });
      setResult(data.result);
    } catch (err) {
      setError(formatApiErrorDetail(err) || "Ad brief failed — please retry.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardShell>
      <div className="max-w-4xl mx-auto px-4 py-10" data-testid="ad-generator-page">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-3">
            <Megaphone className="w-3.5 h-3.5" /> Guided · Ad Generator
          </div>
          <h1 className="font-display text-4xl font-bold text-[#F4F4F0]">Ad Generator</h1>
          <p className="text-[#A0A0A0] text-sm mt-2 max-w-2xl">
            Answer five quick questions. We'll write the script, hook, scene-by-scene visual prompts and a credit estimate — render-ready in seconds.
          </p>
        </div>

        {!result && (
          <>
            <div className="space-y-5 border border-[#1F1F1F] bg-[#0A0A0A] p-6">
              <Field label="Product / service" required>
                <input
                  data-testid="ad-product"
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                  placeholder="e.g. matte black titanium watch · vegan lipstick · cold-brew coffee subscription"
                  className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2.5 text-sm text-[#F4F4F0]"
                />
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Selector label="Category"   value={category} options={CATEGORIES} onChange={setCategory} testId="ad-category" />
                <Selector label="Tone"       value={tone}     options={TONES}      onChange={setTone}     testId="ad-tone" />
                <Selector label="Format"     value={format}   options={FORMATS}    onChange={setFormat}   testId="ad-format" />
                <Selector label="Platform"   value={platform} options={PLATFORMS}  onChange={setPlatform} testId="ad-platform" />
                <Selector label="Length"     value={duration} options={DURATIONS}  onChange={setDuration} testId="ad-duration" />
                <Field label="Target audience">
                  <input
                    data-testid="ad-audience"
                    value={audience}
                    onChange={(e) => setAudience(e.target.value)}
                    placeholder="e.g. urban millennials, brides-to-be, gym founders"
                    className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2.5 text-sm text-[#F4F4F0]"
                  />
                </Field>
              </div>

              <Field label="Visual style (optional)">
                <input
                  data-testid="ad-style"
                  value={style}
                  onChange={(e) => setStyle(e.target.value)}
                  placeholder="e.g. desaturated cinematic · 90s VHS · ultra-clean editorial · golden-hour"
                  className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2.5 text-sm text-[#F4F4F0]"
                />
              </Field>

              <Field label="References, brand notes, must-haves (optional)">
                <textarea
                  data-testid="ad-references"
                  value={references}
                  onChange={(e) => setReferences(e.target.value)}
                  placeholder="Drop product specs, brand voice, tagline you want to keep, or a competitor ad you want to evoke."
                  rows={3}
                  className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2.5 text-sm text-[#F4F4F0] resize-none"
                />
              </Field>
            </div>

            <CreditCostStrip feature="script_help" quality="pro" label="Ad brief generation" />

            {error && (
              <div data-testid="ad-error" className="my-4 px-4 py-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm">
                {error}
              </div>
            )}

            <button
              onClick={onGenerate}
              disabled={busy}
              data-testid="ad-generate-button"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#F0C84B] text-[#080808] font-bold py-3.5 text-sm uppercase tracking-wider transition-colors disabled:opacity-50"
            >
              {busy
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Writing your ad…</>
                : <><Wand2 className="w-4 h-4" /> Generate Ad Brief</>}
            </button>
          </>
        )}

        {result && <AdResult result={result} onReset={() => { setResult(null); setError(""); }} />}
      </div>
    </DashboardShell>
  );
}

function AdResult({ result, onReset }) {
  return (
    <div data-testid="ad-result" className="space-y-6">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">Ad brief ready</p>
          <h2 className="font-display text-2xl font-bold text-[#F4F4F0] mt-1">{result.tagline || "Untitled Ad"}</h2>
        </div>
        <button
          onClick={onReset}
          data-testid="ad-reset"
          className="text-[10px] uppercase tracking-wider text-[#A0A0A0] hover:text-[#D4AF37] border border-[#2A2A2A] hover:border-[#D4AF37] px-3 py-1.5"
        >
          ← Start over
        </button>
      </header>

      <Section title="Concept">{result.concept}</Section>
      <Section title="Hook (first 3s)"><span className="text-[#F4F4F0] font-display text-lg">{result.hook}</span></Section>
      <Section title="Voiceover script">
        <pre className="font-mono text-sm text-[#F4F4F0] whitespace-pre-wrap leading-relaxed">{result.script}</pre>
      </Section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="Music direction" compact>{result.music_direction}</Section>
        <Section title="Visual style" compact>{result.visual_style}</Section>
      </div>

      <div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#A0A0A0] mb-3">
          Scene breakdown — {result?.scenes?.length || 0} shot{(result?.scenes?.length || 0) === 1 ? "" : "s"}
        </p>
        <div className="space-y-3">
          {(result.scenes || []).map((s, i) => (
            <div key={s.id || i} data-testid={`ad-scene-${i + 1}`} className="border border-[#1F1F1F] bg-[#0A0A0A] p-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className="font-display text-sm font-bold text-[#F4F4F0]">
                  <span className="text-[#D4AF37] mr-2">{String(i + 1).padStart(2, "0")}</span>
                  {s.title}
                </p>
                <span className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">{s.duration_s}s</span>
              </div>
              {s.vo && <p className="text-[12px] text-[#D4AF37] mb-2">VO · "{s.vo}"</p>}
              <p className="text-sm text-[#F4F4F0] leading-relaxed">{s.visual_prompt}</p>
              {s.camera && <p className="text-[11px] text-[#A0A0A0] mt-2">Camera · {s.camera}</p>}
              <div className="mt-3 flex flex-wrap gap-2">
                <Link
                  to={`/generate/video?prompt=${encodeURIComponent(s.visual_prompt || "")}&duration=${Math.min(60, Math.max(4, s.duration_s || 4))}`}
                  data-testid={`ad-scene-render-${i + 1}`}
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-3 py-1.5 border border-[#D4AF37]/50 text-[#D4AF37] hover:border-[#D4AF37]"
                >
                  <Sparkles className="w-3 h-3" /> Render this shot
                </Link>
                <Link
                  to={`/generate/image?prompt=${encodeURIComponent(s.visual_prompt || "")}`}
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-3 py-1.5 border border-[#2A2A2A] text-[#A0A0A0] hover:border-[#D4AF37] hover:text-[#D4AF37]"
                >
                  As image
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      {result.render_plan && (
        <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-5">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-2">Render plan</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Shots"            value={result.render_plan.shot_count} />
            <Stat label="Total seconds"    value={`${result.render_plan.total_seconds}s`} />
            <Stat label="Draft credits"    value={`≈ ${result.render_plan.estimated_video_credits_draft}`} />
            <Stat label="Cinema credits"   value={`≈ ${result.render_plan.estimated_video_credits_cinema}`} />
          </div>
          <p className="text-[10px] text-[#A0A0A0] mt-3">
            Estimates use the live PAYG matrix (16 cr / 10s standard · 36 cr / 10s cinema). Failed renders auto-refund.
          </p>
        </div>
      )}
    </div>
  );
}

function Section({ title, children, compact }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#A0A0A0] mb-2">{title}</p>
      <div className={`border border-[#1F1F1F] bg-[#0A0A0A] ${compact ? "px-4 py-3" : "p-5"} text-sm text-[#F4F4F0] leading-relaxed`}>
        {children}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">{label}</p>
      <p className="font-display text-2xl font-bold text-[#F4F4F0] mt-1">{value}</p>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2">
        {label} {required && <span className="text-[#D4AF37]">*</span>}
      </span>
      {children}
    </label>
  );
}

function Selector({ label, value, options, onChange, testId }) {
  return (
    <Field label={label}>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(typeof options[0].id === "number" ? Number(e.target.value) : e.target.value)}
        className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2.5 text-sm text-[#F4F4F0]"
      >
        {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </Field>
  );
}
